export function requiresComparisonBuild(stateWithTraits: CanonicalStateWithTraits): boolean {
    if (stateWithTraits.canonicalInfo.businessProcessType === 'PS01') {
        return !!stateWithTraits.canonicalInfo.postSentInfo.latestCarrierEditCreatedAt;
    }
    return !!stateWithTraits.canonicalInfo.postSentInfo.latestCarrierEditCreatedAt && !stateWithTraits.editTraits.currentUserCanApplyCarrierEdit;
}

function isEmptyFormValue(value: string | boolean | number | Date): boolean {
    return typeof value === 'undefined' || value === null || value === '';
}
function isNullOrUndefined(value: any): boolean {
    return typeof value === 'undefined' || value === null;
}

const primitiveTypes = ['string', 'boolean', 'number'];
function isPrimitiveValue(value: any): boolean {
    return primitiveTypes.indexOf(typeof value) !== -1;
}
export function isPrimitiveComparison(value: any): boolean {
    return (
        value.hasOwnProperty('status') &&
        value.hasOwnProperty('changedOn') &&
        value.hasOwnProperty('initialValue') &&
        value.hasOwnProperty('currentValue') &&
        !!value.status
    );
}
function isLiteralComparison(value: any): boolean {
    return value.hasOwnProperty('status') && value.hasOwnProperty('changedOn') && value.hasOwnProperty('changedFields') && !!value.status;
}
/**
 * Перебирает все поля переданного объекта, оценивая их как Comparison<T>.
 * Если хотя бы одно поле имеет статус отличный от NotChanged, то возвращает true.
 */
function getComplexObjectComparisonStatus(comparison: any): ComparisonStatus {
    for (const key of Object.keys(comparison || {})) {
        const field = comparison[key];
        if (field && field.hasOwnProperty('status')) {
            if (isPrimitiveComparison(field)) {
                if (field.status !== 'NotChanged') {
                    return 'Changed';
                }
            } else if (isLiteralComparison(field)) {
                const status = getComplexObjectComparisonStatus(field.changedFields);
                if (status !== 'NotChanged') {
                    return 'Changed';
                }
            } else {
                throw new Error('Для определения статуса comparison использован объект, содержащий неопознанные данные');
            }
        }
    }
    return 'NotChanged';
}
export function toArrayComparison<T>(
    latestEdit: T[],
    initialState: T[],
    changedOn: string,
    compareFn: (latestValue: T, initialValue: T) => boolean
): ObjectComparison[] {
    const result: ObjectComparison[] = [];
    const changedItems: T[] = [];
    // ищем отредактированные и добавленные элементы
    latestEdit.forEach(latestItem => {
        let possiblyChangedItem: T | null = null;
        initialState.forEach(initialItem => {
            if (compareFn(latestItem, initialItem)) {
                possiblyChangedItem = initialItem;
            }
        });
        if (possiblyChangedItem) {
            result.push(toLiteralObjectComparison(possiblyChangedItem, latestItem, changedOn));
            changedItems.push(possiblyChangedItem);
        } else {
            result.push({
                changedFields: <any>{},
                changedOn,
                status: 'Added'
            });
        }
    });
    // ищем удаленные элементы
    initialState.forEach(initialItem => {
        if (changedItems.findIndex(changedItem => compareFn(changedItem, initialItem)) === -1) {
            result.push({
                changedFields: <any>{},
                changedOn,
                status: 'Removed'
            });
        }
    });

    return result;
}
/* eslint-disable-next-line complexity */
export function toLiteralObjectComparison(initialVersion: any, latestVersion: any, changedOn: string): any {
    const resultedComparison = {
        changedFields: {},
        changedOn,
        status: 'NotChanged'
    } as ObjectComparison;

    if (isNullOrUndefined(initialVersion) && isNullOrUndefined(latestVersion)) {
        return resultedComparison;
    }
    const normalizedBaseObject = initialVersion || {};
    const normalizedCurrentObject = latestVersion || {};
    const objectToInspect = isNullOrUndefined(initialVersion) ? latestVersion : initialVersion;

    for (const key of Object.keys(objectToInspect)) {
        if (isPrimitiveValue(normalizedCurrentObject[key]) || isPrimitiveValue(normalizedBaseObject[key])) {
            resultedComparison.changedFields[key] = toPrimitiveComparison(normalizedCurrentObject[key], normalizedBaseObject[key], changedOn);
            continue;
        }
        if (isNullOrUndefined(normalizedCurrentObject[key]) && isNullOrUndefined(normalizedBaseObject[key])) {
            resultedComparison.changedFields[key] = toPrimitiveComparison(normalizedCurrentObject[key], normalizedBaseObject[key], changedOn);
            continue;
        }
        if (normalizedCurrentObject[key] instanceof Date || normalizedBaseObject[key] instanceof Date) {
            resultedComparison.changedFields[key] = toDateComparison(normalizedCurrentObject[key], normalizedBaseObject[key], changedOn);
            continue;
        }

        if (normalizedCurrentObject[key] instanceof Object || normalizedBaseObject[key] instanceof Object) {
            resultedComparison.changedFields[key] = toLiteralObjectComparison(normalizedBaseObject[key], normalizedCurrentObject[key], changedOn);
            continue;
        }
        throw new Error('Функции toLiteralObjectComparison передан неопознанный тип данных');
    }
    resultedComparison.status = getComplexObjectComparisonStatus(resultedComparison.changedFields);
    return resultedComparison;
}

export function toPrimitiveComparison(
    currentVersion: string | boolean | number,
    baseVersion: string | boolean | number,
    changedOn: string
): PrimitiveComparison {
    let status: ComparisonStatus = 'Changed';
    if (!isEmptyFormValue(currentVersion) && !isEmptyFormValue(baseVersion) && currentVersion === baseVersion) {
        status = currentVersion === baseVersion ? 'NotChanged' : 'Changed';
    }
    if (isEmptyFormValue(currentVersion) && isEmptyFormValue(baseVersion)) {
        status = 'NotChanged';
    }

    return { status, initialValue: baseVersion, currentValue: currentVersion, changedOn };
}
export function toDateComparison(currentVersion: Date, baseVersion: Date, changedOn: string): PrimitiveComparison {
    let status: ComparisonStatus = 'Changed';
    if (!isEmptyFormValue(currentVersion) && !isEmptyFormValue(baseVersion)) {
        status = currentVersion.getTime() === baseVersion.getTime() ? 'NotChanged' : 'Changed';
    }
    if (isEmptyFormValue(currentVersion) && isEmptyFormValue(baseVersion)) {
        status = 'NotChanged';
    }

    return { status, initialValue: baseVersion, currentValue: currentVersion, changedOn };
}
